import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React from 'react';
import { QRCodeSVG } from 'qrcode.react';
import styled from 'styled-components';
import i18next from 'i18next';
import { useTheme } from '@famly/mf_theme';
import { Text } from '@famly/mf_data-display_text';
import { Base } from 'web-app/react/components/layout/layout';
import ProfileImage from 'web-app/react/components/profile-image/profile-image';
import * as Breakpoints from 'web-app/styleguide/breakpoint-helpers';
import { hslStringToHex } from '@famly/vet_ts-utils_generic-hooks';
import translate from 'signin-app/helpers/translate';
import { SettingsDropdown } from 'signin-app/components/settings-dropdown';
import { useResilientQuery } from '@famly/stat_apollo-query';
import { useWhitelabelConfiguration } from 'signin-app/whitelabel/use-whitelabel-configuration';
import { Spinner } from '@famly/mf_feedback_spinner';
import { createStack, Stack } from '@famly/mf_layout_stack';
import { GuestEntry } from 'signin-app/guest/guest-entry';
import RoutesMap from 'signin-app/routes/routes-map';
import * as Queries from './queries';
import { useGenerateQrValue, useGenerateSecret } from './hooks';
export const QrScreen = () => {
    var _a;
    const { data: institutionData } = useResilientQuery(Queries.GetInstitutionData);
    const whitelabelConfiguration = useWhitelabelConfiguration();
    const institution = React.useMemo(() => {
        if (institutionData && institutionData.institutions.length > 0) {
            // For the sign in user, the query will return only one institution
            return institutionData.institutions[0];
        }
        return undefined;
    }, [institutionData]);
    return (_jsxs(StyledQRView, { "data-e2e-id": "QR-generator-view", children: [_jsxs(LogoutContainer, { alignItems: "center", gap: 4, children: [_jsx(GuestEntry, { returnTo: RoutesMap.qr }), _jsx(SettingsDropdown, { showWarning: true, siteName: institution === null || institution === void 0 ? void 0 : institution.title })] }), _jsxs(InstitutionContainer, { children: [_jsx(ProfileImage, { src: (_a = institution === null || institution === void 0 ? void 0 : institution.profileImage) === null || _a === void 0 ? void 0 : _a.url, size: 80, placeholderSrc: "img/empty-institution.jpg" }), _jsx(Text, { variant: "h6", color: whitelabelConfiguration.LoginPagesFontColor, emphasized: true, children: institution === null || institution === void 0 ? void 0 : institution.title })] }), _jsx(TextContainer, { children: _jsx(Text, { variant: "h5", marginBottom: "10px", color: whitelabelConfiguration.LoginPagesFontColor, children: translate('qrCodeExplanation') }) }), _jsx(QRCode, { institutionId: institution === null || institution === void 0 ? void 0 : institution.institutionId })] }));
};
export const QRCode = ({ institutionId }) => {
    const modernFamlyTheme = useTheme();
    const [secret, setSecret] = React.useState('');
    const [qrValue, setQrValue] = React.useState('');
    const whitelabelConfiguration = useWhitelabelConfiguration();
    const customP500 = whitelabelConfiguration.p500;
    useGenerateSecret(institutionId, setSecret);
    useGenerateQrValue(institutionId, setQrValue, secret);
    return (_jsx(QRCodeContainer, { children: !institutionId || !qrValue ? (_jsx(Stack, { justifyContent: "center", width: "100%", children: _jsx(Spinner, { size: 32, color: "n0", "aria-label": i18next.t($ => $.react.components.loading.spinner.translations.json.loadingAriaLabel) }) })) : (_jsx(QRCodeSVG, { value: qrValue, fgColor: customP500 ? customP500 : hslStringToHex(modernFamlyTheme.colorPalette.p500), size: 160, "data-e2e-id": "qr-code", "data-e2e-qrcode-value": qrValue })) }));
};
const StyledQRView = styled(createStack({
    alignItems: 'center',
    direction: 'column',
    height: '100%',
    minHeight: '100vh',
    position: 'relative',
    width: '100%',
})) `
    background-image: url("data:image/svg+xml,%3Csvg width='1194' height='834' viewBox='0 0 1194 834' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cg style='mix-blend-mode:overlay' opacity='0.2'%3E%3Cpath d='M1239.38 2.24008C361.248 98.6683 320.29 901.326 477.249 875.07C634.209 848.813 231.236 257.223 -252.813 471.442' stroke='%23F4EDFC' stroke-width='11' stroke-linecap='round'/%3E%3C/g%3E%3C/svg%3E");
    background-attachment: fixed;
    background-repeat: no-repeat;
    background-size: cover;
    background-position: bottom center;
`;
const LogoutContainer = styled(createStack({ position: 'absolute', right: 0, top: 0 })) `
    && {
        padding: 20px;
    }

    ${Breakpoints.tabletLandscapeAndLarger `
        && {
            padding: 40px;
        }
    `};
`;
const InstitutionContainer = styled(Base) `
    max-width: 310px;
    text-align: center;
    margin-top: 70px;
    margin-bottom: 75px;
`;
const QRCodeContainer = styled(Base) `
    box-sizing: border-box;
    background-color: ${props => props.theme.mf.colorPalette.n0};
    padding: 43px;
    border-radius: 12px;
    width: 246px;
    height: 250px;
`;
export const TextContainer = styled(Base) `
    max-width: 310px;
    text-align: center;
    margin-bottom: 24px;
`;
